import * as React from 'react';
import { connect } from 'react-redux';
import { clone, get, isEqual, isNil } from 'lodash';

import AddNew from 'components/buttons/add-new/AddNew';
import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import InputLocation from 'components/inputs/input-location/InputLocation';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { danger } from 'redux-store/reducers/common-reducer';

import { isLesserThanOrEqualTo } from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';

import type {
  AdminAnalytic,
  DeliverableTikTokAnalytic,
  LocationSuggestionCityOrCountry,
  StatisticsReachByCountry,
  TextEntryKeyboardEvent
} from 'types';

applyTranslations(require('./deliverables-metrics-reached-countries-form.i18n.json'));

import './deliverables-metrics-reached-countries-form.scss';

import type { ChangeEvent } from 'react';

type Analytic = {
  reachByCountry: Array<StatisticsReachByCountry>,
  // Because of difference Analytic (for TikTok) and AdminAnalytic
  impressions?: number | null,
  metricsType: string
} & AdminAnalytic;

type OwnProps = {
  deliverableId: number,
  campaignId: number,
  analytic: DeliverableTikTokAnalytic | undefined | null,
  onUpdate: (c: number, b: number, a: AdminAnalytic) => Promise<void>
};

type DispatchProps = {
  dispatchDanger: (a: string) => Promise<void>
};

type Props = OwnProps & DispatchProps;

type State = {
  metric: Analytic,
  isChanged: boolean
};

const getNewCountryMetric = () => ({
  code: '',
  country: '',
  percent: 0
});

class DeliverablesMetricsReachedCountriesForm extends React.Component<Props, State> {
  form: Form | undefined | null;

  constructor(props: Props) {
    super(props);

    this.state = {
      metric: this.getDefaultMetricState(this.props),
      isChanged: false
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (!isEqual(this.props.analytic, prevProps?.analytic)) {
      this.setState({ // eslint-disable-line react/no-did-update-set-state
        metric: this.getDefaultMetricState(this.props)
      }, () => this.validateInput());
    }
  }

  getDefaultMetricState = (props: Props) => {
    const reachByCountry = clone(get(props, 'analytic.reachByCountry', [] as Array<any>));

    if (reachByCountry.length === 0) {
      reachByCountry.push(getNewCountryMetric());
    }

    return ({
      reachByCountry: reachByCountry.map(item => ({
        code: item.code,
        country: item.country,
        percent: item.percent
      })),
      // Because of difference Analytic (for TikTok) and AdminAnalytic
      impressions: undefined,
      metricsType: 'tiktok_video_metrics'
    });
  };

  handleAddNewMetric = () => {
    this.setState((state) => ({
      metric: {
        ...state.metric,
        reachByCountry: [...state.metric.reachByCountry, getNewCountryMetric()]
      }
    }));
  };

  handleRemoveMetric = (index: number) => {
    this.setState((state) => {
      const reachByCountryUpdated = [...state.metric.reachByCountry];
      reachByCountryUpdated.splice(index, 1);

      return {
        metric: {
          ...state.metric,
          reachByCountry: reachByCountryUpdated
        },
        isChanged: true
      };
    }, this.handleSubmit);
  };

  handleSubmit = () => {
    const { deliverableId, campaignId, onUpdate, dispatchDanger } = this.props;
    const { metric, isChanged } = this.state;
    const isInputValid = this.validateInput();

    if (!isInputValid) {
      return;
    }

    if (isChanged) {
      onUpdate(deliverableId, campaignId, {
        ...metric,
        shares: undefined,
        profileVisits: undefined
      })
        .then(() => this.setState({ isChanged: false }))
        .catch(() => dispatchDanger(__t('deliverables_metrics_reached_countries_form.save_error')));
    }
  };

  handleReachCountryChange = (
    event: TextEntryKeyboardEvent,
    location: LocationSuggestionCityOrCountry,
    position: number
  ): void => {
    const { dispatchDanger } = this.props;
    const { reachByCountry } = this.state.metric;

    if (reachByCountry.some((item, index) => item.code === location.countryCode && index !== position)) {
      dispatchDanger(__t('deliverables_metrics_reached_countries_form.existed_country_error'));
      return;
    }

    this.setState(state => ({
      metric: {
        ...state.metric,
        reachByCountry: state.metric.reachByCountry.map((item, id) => {
          if (id !== position) {
            return item;
          }

          return {
            ...item,
            code: location.countryCode,
            country: location.country
          };
        })
      },
      isChanged: true
    }), () => this.handleSubmit());
  };

  handleReachPercentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const input = event.target;
    const position = Number(input.name);

    this.setState(state => ({
      metric: {
        ...state.metric,
        reachByCountry: state.metric.reachByCountry.map((item, id) => {
          if (id !== position) {
            return item;
          }

          return {
            ...item,
            percent: input.value && Number(input.value) ? Number(input.value) : 0
          };
        })
      },
      isChanged: true
    }));
  };

  validateInput = (triggerWarningPopup: boolean = true) => {
    const { dispatchDanger } = this.props;
    const isFormValid = Boolean(this.form && this.form.isFormValid());

    if (isFormValid) {
      return true;
    }

    if (triggerWarningPopup) {
      dispatchDanger(__t('deliverables_metrics_reached_countries_form.reach_percentage_exceed'));
    }

    return false;
  };

  renderReachedCountryForm = (selectedMetric: StatisticsReachByCountry, id: number) => {
    const { metric } = this.state;

    return (
      <div
        className="deliverables-metrics-reached-countries-form__country"
        key={`deliverables-metrics-reached-countries-form__country-${selectedMetric.code || id}`}
      >
        <FormField
          label={__t('deliverables_metrics_reached_countries_form.label_country')}
          fontSize="small"
          labelType="highlighted"
          direction="column"
          showValidationIcon={false}
          inline
        >
          <div className="deliverables-metrics-reached-countries-form__country-name">
            <InputLocation
              name={String(id)}
              value={selectedMetric.country}
              valueType="country"
              suggestionType="country"
              onChange={(e, location) => this.handleReachCountryChange(e, location, id)}
            />
          </div>
        </FormField>
        <div className="deliverables-metrics-reached-countries-form__country-value">
          <FormField
            label={__t('deliverables_metrics_reached_countries_form.label_reach_percentage')}
            fontSize="small"
            labelType="highlighted"
            direction="column"
            showValidationIcon={false}
            inline
            validators={[
              isLesserThanOrEqualTo(
                metric.reachByCountry.reduce((res, i) => res + (i?.percent ? Number(i?.percent) : 0), 0),
                100,
                __t('deliverables_metrics_reached_countries_form.reach_percentage_exceed')
              )
            ]}
          >
            <Input
              type="text"
              name={String(id)}
              width="responsive"
              value={isNil(selectedMetric.percent) ? '' : String(selectedMetric.percent)}
              onChange={this.handleReachPercentChange}
              onBlur={this.handleSubmit}
            />
          </FormField>
        </div>
        {
          metric.reachByCountry.length > 1 && (
            <div
              className="deliverables-metrics-reached-countries-form__action-remove"
            >
              <SVGDynamicIcon
                name="cross-custom-fg"
                customColor="grey"
                width={14}
                height={14}
                onClick={() => this.handleRemoveMetric(id)}
              />
            </div>
          )
        }
      </div>
    );
  };

  render() {
    const { metric } = this.state;

    return (
      <section className="deliverables-metrics-reached-countries-form">
        <Form
          ref={(form?: Form | null): void => {
            this.form = form;
          }}
        >
          {
            metric.reachByCountry.map((item, id) => this.renderReachedCountryForm(item, id))
          }
          <div
            className="deliverables-metrics-reached-countries-form__action-add"
          >
            <AddNew
              color="grey"
              text="Add another"
              icon={{
                name: 'plus-inside-circle-custom-bg-white-fg',
                width: 16,
                height: 16,
                distance: 'double'
              }}
              onClick={this.handleAddNewMetric}
            />
          </div>
        </Form>
      </section>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchDanger: message => dispatch(danger(message, { hideDuration: 2000 }))
});

export default connect(null, mapDispatchToProps)(DeliverablesMetricsReachedCountriesForm);
